import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Grid from "../../components/Grid/Grid";
import { CUSTOM_MESSAGES, PAGE_SIZES } from "../../constants/globalConstants";
import { Layout } from "../../layout/PortalLayout/PortalLayout";
import MainHeading from "../../components/MainHeading/MainHeading";
import CustomButton from "../../components/CustomButton/CustomButton";
import ThreeDotActionItem from "../../components/ThreeDotActionItem/ThreeDotActionItem";
import useGridHeight from "../../hooks/useGridHeight";
import { useLazyGetBrandListQuery } from "../../app/api/brandApi";
import { useLazyGetFamilyListQuery } from "../../app/api/familyApi";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { setAsyncFormSelector } from "../../redux/slice/persist/dropdownSelector";
import { handleApiResponse } from "../../utils/handleApiResponse";

const FamilyPortal = () => {
  useGridHeight();
  const [tableData, setTableData] = useState<any[]>([]);
  const persistDropdownSelector = useAppSelector(
    (state) => state.dropdownSelector
  );
  const [brandId, setBrandId] = useState<string | null>(
    persistDropdownSelector["brand_selector"]?.value ?? null
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [getBrandListQuery] = useLazyGetBrandListQuery();
  const [getFamilyListQuery, familyListResponse] = useLazyGetFamilyListQuery();

  const pageSizeRef = useRef(String(PAGE_SIZES.Default_List_Size));
  const tableHeaders = [
    {
      headerName: "Name",
      shouldSort: true,
    },
    {
      headerName: "Unique ID",
      shouldSort: true,
    },
    {
      headerName: "Brand Name",
      shouldSort: true,
    },
    {
      headerName: "Status",
    },
    { headerName: "" },
  ];

  const asyncFormSelectProps = [
    {
      label: "Brand",
      name: "brand_name",
      onChange: (val: string | null, label: string) => {
        setBrandId(val);
        dispatch(
          setAsyncFormSelector({
            name: "brand_selector",
            value: val,
            label: label,
          }),
          "setAsyncFormSelector"
        );
      },
      fetchQuery: getBrandListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
      },
      optionLabelName: "brand_name",
      placeholder: "Select Brand",
      shouldFetch: true,
      persistedOption: persistDropdownSelector["brand_selector"]
        ? {
            label: persistDropdownSelector["brand_selector"]?.label,
            value: persistDropdownSelector["brand_selector"]?.value,
          }
        : null,
    },
  ];

  const handleGetFamilyList = async () => {
    try {
      const response = await getFamilyListQuery({
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
      }).unwrap();

      if (response?.status_code === 200) {
        setTableData(response?.data);
      }
    } catch (error: any) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Fetch Family List Error",
      });
    }
  };

  useEffect(() => {
    if (brandId) {
      handleGetFamilyList();
    }
    // eslint-disable-next-line
  }, [brandId]);

  const updatedTableData = (tableData ?? []).map((tdata) => ({
    Name: tdata.family_name,
    "Unique ID": tdata.id,
    "Brand Name": tdata.brand_name,
    Status: [
      tdata.is_active
        ? { text: "Active", color: "#12da85" }
        : { text: "Inactive", color: "#bbbbbb" },
    ],
    actionIcon: (
      <ThreeDotActionItem
        actionItemsMenuPositionStyles={{
          right: "15px",
          left: "unset",
        }}
        actionIconSubItemsList={[
          {
            actionItemName: "View Details",
            onClick: (id: string) =>
              navigate(`/provisioning/family/create?id=${tdata.id}`),
          },
          {
            actionItemName: "Edit Details",
            onClick: (id: string) =>
              navigate(`/provisioning/family/edit?id=${tdata.id}`),
          },
        ]}
        id={tdata.id}
      />
    ),
  }));

  const handleLoadMoreClick = async () => {
    try {
      if (
        familyListResponse.isSuccess &&
        familyListResponse.data?.next_start_key
      ) {
        const response = await getBrandListQuery(
          {
            startKey: familyListResponse.data?.next_start_key ?? "",
            page_size: pageSizeRef.current,
          },
          true
        );

        if (response && response?.data?.data) {
          setTableData((prev) => [...prev, ...response?.data?.data]);
        }
      }
    } catch (error) {
      console.error(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Load More Family List Error",
      });
    }
  };

  return (
    <>
      <Layout>
        <MainHeading headerText="Family" subHeaderText={"Description"}>
          <CustomButton
            isSubmitting={false}
            label="Create Family"
            customClass="w-11rem px-1 py-0 m-0 h-48 fs-6"
            onClick={() => navigate("create")}
            disabled={false}
          />
        </MainHeading>

        <Grid
          tableHeaders={tableHeaders}
          tableData={updatedTableData}
          isLoading={familyListResponse.isFetching}
          isDataPresent={familyListResponse.data?.data?.length > 0}
          isError={familyListResponse.isError}
          error={familyListResponse.error}
          noDataUIMainText={CUSTOM_MESSAGES.No_Family_Yet}
          noDataUISubText={CUSTOM_MESSAGES.Create_Family_Now}
          noDataUIButtonLabel="Create Family"
          showPageSizeSelector
          page_size={pageSizeRef.current}
          handlePageSizeChange={(val) => {
            if (val !== pageSizeRef.current) {
              pageSizeRef.current = val as string;
              handleGetFamilyList();
            }
          }}
          sort={{ sortField: "Name", sortOrder: "asc" }}
          handleClick={() => navigate("create")}
          asyncFormSelectProps={asyncFormSelectProps}
        />
        {((familyListResponse.isSuccess &&
          familyListResponse?.data?.next_start_key) ||
          familyListResponse.isFetching) && (
          <div className="text-center" id="loadmore-button">
            <CustomButton
              isSubmitting={familyListResponse.isFetching}
              label="Load More"
              customClass="px-1 mw-100 py-0 m-0 mt-3 fs-6"
              customStyles={{ width: "30rem" }}
              onClick={handleLoadMoreClick}
            />
          </div>
        )}
      </Layout>
    </>
  );
};

export default FamilyPortal;
